import { useMemo } from "react";
import type { TypeDefinition } from "~/api/fga-types";

interface Node {
  id: string;
  x: number;
  y: number;
}

interface Edge {
  from: string;
  to: string;
  relations: string[];
}

function extractGraph(typeDefs: TypeDefinition[]) {
  const nodeIds = typeDefs.map((td) => td.type);
  const edgeMap = new Map<string, string[]>();

  for (const td of typeDefs) {
    const metadata = td.metadata?.relations ?? {};
    for (const [relation, meta] of Object.entries(metadata)) {
      for (const ut of meta.directly_related_user_types ?? []) {
        const sourceLabel = ut.relation ? `${ut.type}#${ut.relation}` : ut.type;
        const sourceNode = ut.type;
        const key = `${sourceNode}\0${td.type}`;
        if (!edgeMap.has(key)) edgeMap.set(key, []);
        const label = ut.relation
          ? `${relation} (via ${sourceLabel})`
          : relation;
        edgeMap.get(key)?.push(label);
      }
    }
  }

  const edges: Edge[] = Array.from(edgeMap.entries()).map(
    ([key, relations]) => {
      const [from = "", to = ""] = key.split("\0");
      return { from, to, relations };
    },
  );

  return { nodeIds, edges };
}

function layoutNodes(nodeIds: string[], width: number, height: number): Node[] {
  if (nodeIds.length === 1) {
    return [{ id: nodeIds[0] ?? "", x: width / 2, y: height / 2 }];
  }

  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.min(width, height) * 0.35;

  return nodeIds.map((id, i) => {
    const angle = (2 * Math.PI * i) / nodeIds.length - Math.PI / 2;
    return {
      id,
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
    };
  });
}

const NODE_W = 120;
const NODE_H = 40;
const ARROW_ID = "arrowhead";
const SELF_LOOP_R = 30;

export default function ModelGraph({
  typeDefinitions,
}: {
  typeDefinitions: TypeDefinition[];
}) {
  const { nodeIds, edges } = useMemo(
    () => extractGraph(typeDefinitions),
    [typeDefinitions],
  );

  const width = Math.max(400, nodeIds.length * 100);
  const height = Math.max(300, nodeIds.length * 80);

  const nodes = useMemo(
    () => layoutNodes(nodeIds, width, height),
    [nodeIds, width, height],
  );

  const nodeMap = useMemo(() => {
    const m = new Map<string, Node>();
    for (const n of nodes) m.set(n.id, n);
    return m;
  }, [nodes]);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Authorization model graph"
      style={{
        width: "100%",
        maxHeight: 500,
        backgroundColor: "var(--bf-base-2-bg)",
        borderRadius: 4,
      }}
    >
      <defs>
        <marker
          id={ARROW_ID}
          viewBox="0 0 10 6"
          refX="10"
          refY="3"
          markerWidth="8"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 3 L 0 6 Z" fill="var(--bf-base-3-color, #666)" />
        </marker>
      </defs>

      {edges.map((edge) => {
        const from = nodeMap.get(edge.from);
        const to = nodeMap.get(edge.to);
        if (!from || !to) return null;

        // Self-loop
        if (edge.from === edge.to) {
          const cx = from.x;
          const cy = from.y - NODE_H / 2 - SELF_LOOP_R;
          return (
            <g key={`${edge.from}-${edge.to}`}>
              <path
                d={`M ${from.x - 12} ${from.y - NODE_H / 2}
                    C ${cx - SELF_LOOP_R * 1.5} ${cy - SELF_LOOP_R},
                      ${cx + SELF_LOOP_R * 1.5} ${cy - SELF_LOOP_R},
                      ${from.x + 12} ${from.y - NODE_H / 2}`}
                fill="none"
                stroke="var(--bf-base-3-color, #666)"
                strokeWidth={1.5}
                markerEnd={`url(#${ARROW_ID})`}
              />
              <text
                x={cx}
                y={cy - SELF_LOOP_R + 4}
                textAnchor="middle"
                fontSize={10}
                fill="var(--bf-base-3-color, #888)"
              >
                {edge.relations.join(", ")}
              </text>
            </g>
          );
        }

        // Direction vector from center to center
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist === 0) return null;

        const nx = dx / dist;
        const ny = dy / dist;

        // Start/end at the edge of the node box
        const sx = from.x + nx * (NODE_W / 2);
        const sy = from.y + ny * (NODE_H / 2);
        const ex = to.x - nx * (NODE_W / 2);
        const ey = to.y - ny * (NODE_H / 2);

        // Check if there's a reverse edge, and if so offset the curve
        const hasReverse = edges.some(
          (e) => e.from === edge.to && e.to === edge.from,
        );
        const offset = hasReverse ? 20 : 0;

        // Perpendicular for curve offset
        const px = -ny * offset;
        const py = nx * offset;
        const mx = (sx + ex) / 2 + px;
        const my = (sy + ey) / 2 + py;

        // Label position
        const lx = (sx + ex) / 2 + px * 0.7;
        const ly = (sy + ey) / 2 + py * 0.7 - 6;

        return (
          <g key={`${edge.from}-${edge.to}`}>
            {offset === 0 ? (
              <line
                x1={sx}
                y1={sy}
                x2={ex}
                y2={ey}
                stroke="var(--bf-base-3-color, #666)"
                strokeWidth={1.5}
                markerEnd={`url(#${ARROW_ID})`}
              />
            ) : (
              <path
                d={`M ${sx} ${sy} Q ${mx} ${my} ${ex} ${ey}`}
                fill="none"
                stroke="var(--bf-base-3-color, #666)"
                strokeWidth={1.5}
                markerEnd={`url(#${ARROW_ID})`}
              />
            )}
            <text
              x={lx}
              y={ly}
              textAnchor="middle"
              fontSize={10}
              fill="var(--bf-base-3-color, #888)"
            >
              {edge.relations.join(", ")}
            </text>
          </g>
        );
      })}

      {nodes.map((node) => (
        <g key={node.id}>
          <rect
            x={node.x - NODE_W / 2}
            y={node.y - NODE_H / 2}
            width={NODE_W}
            height={NODE_H}
            rx={6}
            fill="var(--bf-base-bg, #fff)"
            stroke="var(--bfc-brand, #0078d4)"
            strokeWidth={2}
          />
          <text
            x={node.x}
            y={node.y + 1}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={13}
            fontWeight={600}
            fontFamily="monospace"
            fill="var(--bf-base-color, #333)"
          >
            {node.id}
          </text>
        </g>
      ))}
    </svg>
  );
}
